// Measurement tool for measuring distances in glyph canvas
// Activated immediately by holding Tab in text mode or editing mode
import { Logger } from '../logger';
const console = new Logger('MeasurementTool');
export class MeasurementTool {
    constructor(glyphCanvas) {
        // Drag state
        this.isDragging = false;
        this.originX = 0; // Canvas coordinates
        this.originY = 0; // Canvas coordinates
        // Visibility state
        this.visible = false;
        this.cancelledByZoom = false;
        this.disabledForTyping = false;
        this.glyphCanvas = glyphCanvas;
    }
    /**
     * Handle the measurement key press and show the tool immediately.
     */
    handleMeasurementKeyPress() {
        this.cancelledByZoom = false;
        this.showImmediately();
        this.glyphCanvas.updateCursorStyle();
    }
    /**
     * Handle the measurement key release and reset all state.
     */
    handleMeasurementKeyRelease() {
        const wasVisible = this.visible;
        this.visible = false;
        this.cancelledByZoom = false;
        this.isDragging = false;
        this.disabledForTyping = false;
        // Perform hit-testing when measurement tool turns off
        if (wasVisible) {
            if (this.glyphCanvas.outlineEditor.active &&
                document.activeElement === this.glyphCanvas.canvas) {
                this.glyphCanvas.outlineEditor.performHitDetection(null);
            }
            this.glyphCanvas.updateHoveredGlyph();
            this.glyphCanvas.updateCursorStyle();
            this.glyphCanvas.render();
        }
    }
    /**
     * Handle mouse wheel/zoom - cancel or hide tool
     */
    handleWheel() {
        const wasVisible = this.visible;
        this.visible = false;
        this.cancelledByZoom = true;
        // Perform hit-testing when measurement tool turns off
        if (wasVisible) {
            if (this.glyphCanvas.outlineEditor.active &&
                document.activeElement === this.glyphCanvas.canvas) {
                this.glyphCanvas.outlineEditor.performHitDetection(null);
            }
            this.glyphCanvas.updateHoveredGlyph();
            this.glyphCanvas.updateCursorStyle();
        }
    }
    /**
     * Handle mouse down to start dragging measurement line
     */
    handleMouseDown(clientX, clientY, canvasRect) {
        if (!this.glyphCanvas.measurementKeyPressed ||
            !this.glyphCanvas.outlineEditor.active) {
            return false;
        }
        this.isDragging = true;
        this.originX = clientX - canvasRect.left;
        this.originY = clientY - canvasRect.top;
        this.showImmediately();
        return true;
    }
    /**
     * Handle mouse up to stop dragging
     */
    handleMouseUp() {
        this.isDragging = false;
    }
    /**
     * Check if measurement tool should block hit detection
     */
    shouldBlockHitDetection() {
        return ((this.glyphCanvas.measurementKeyPressed &&
            !this.disabledForTyping) ||
            this.isDragging);
    }
    /**
     * Check if crosshair cursor should be shown
     */
    shouldShowCrosshair() {
        return (this.glyphCanvas.measurementKeyPressed &&
            this.glyphCanvas.outlineEditor.active &&
            this.visible &&
            !this.disabledForTyping);
    }
    /**
     * Check if visual crosshair/measurement lines should be drawn
     */
    shouldDrawVisuals() {
        return (this.glyphCanvas.measurementKeyPressed &&
            this.visible &&
            this.glyphCanvas.outlineEditor.active &&
            !this.disabledForTyping);
    }
    /**
     * Check if text mode measurements should be drawn
     */
    shouldDrawTextModeMeasurements() {
        return (this.glyphCanvas.measurementKeyPressed &&
            this.visible &&
            !this.glyphCanvas.outlineEditor.active &&
            !this.disabledForTyping);
    }
    /**
     * Show measurement tool immediately (when dragging starts)
     */
    showImmediately() {
        this.visible = true;
        // Clear hover states when measurement tool turns on
        this.glyphCanvas.outlineEditor.hoveredGlyphIndex = -1;
        this.glyphCanvas.outlineEditor.hoveredComponentIndex = null;
        this.glyphCanvas.outlineEditor.hoveredPointIndex = null;
        this.glyphCanvas.outlineEditor.hoveredAnchorIndex = null;
    }
}
